import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navigation.css';

const Navigation = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});
  const location = useLocation();
  const currentPath = location.pathname;

  const menuItems = [
    {
      label: 'Home',
      icon: '🏠',
      items: [
        { path: '/', label: 'Summary', icon: '📋' }
      ]
    },
    {
      label: 'evoML Platform',
      icon: '🔬',
      items: [
        { path: '/evoml-automated-workflow', label: 'Automated Workflow', icon: '🤖' },
        { path: '/evoml-sdlc', label: 'SDLC Integration', icon: '🔄' },
        { path: '/evoml-use-cases', label: 'Use Cases & Examples', icon: '💡' },
        { path: '/evoml-integrations', label: 'Integrations & APIs', icon: '🔌' },
        { path: '/evoml-architecture', label: 'System Architecture', icon: '🏗️' },
        { path: '/evoml-deployment', label: 'Deployment Architecture', icon: '🚀' },
        { path: '/evoml-governance', label: 'Governance & Compliance', icon: '🏛️' },
        { path: '/evoml-roadmap', label: 'Product Roadmap', icon: '🗺️' }
      ]
    },
    {
      label: 'Artemis',
      icon: '⚡',
      items: [
        { path: '/artemis-overview', label: 'What is Artemis', icon: '⚡' },
        { path: '/artemis-uniqueness', label: 'How Artemis Works', icon: '🧬' },
        { path: '/artemis-software-evolution', label: 'Software Evolution', icon: '🚀' },
        { path: '/artemis-evolution', label: 'Artemis Evolution Examples', icon: '🧬' },
        { path: '/artemis-intelligence-vs-big-models', label: 'Artemis Intelligence vs Big Models', icon: '📊' },
        { path: '/artemis-sdlc', label: 'SDLC Integration', icon: '🔄' },
        { path: '/artemis-file-split', label: 'LLM File Optimization', icon: '📁' },
        { path: 'https://usecases.ngrok.app/', label: 'Real World Use Cases', icon: '🌍', external: true },
        { path: '/artemis-use-cases', label: 'Use Cases & Examples', icon: '💡' },
        { path: '/artemis-integrations', label: 'Integrations & APIs', icon: '🔌' },
        { path: '/artemis-architecture', label: 'System Architecture', icon: '🏗️' },
        { path: '/artemis-governance', label: 'Governance & Compliance', icon: '🏛️' },
        { path: '/artemis-roadmap', label: 'Development Roadmap', icon: '🗺️' }
      ]
    },
    {
      label: 'AI Agents',
      icon: '🤖',
      items: [
        { path: '/artemis-approaches', label: 'Agentic Evolution', icon: '🧬' },
        { path: '/agents-platforms', label: 'Decision Maker Platform', icon: '🎯' },
        { path: '/turintech-auto-feedback', label: 'Turintech Auto Feedback', icon: '🔄' },
        { path: '/optimization-approaches', label: 'Optimization Approaches', icon: '🧬' },
        { path: '/agent-optimisation', label: 'Agent Optimisation', icon: '🚀' },
        { path: '/agent-orchestration', label: 'Agent Orchestration & Swarms', icon: '🌐' }
      ]
    },
    {
      label: 'Admin',
      icon: '🔧',
      items: [
        { path: '/admin/prompts', label: 'Prompt Management', icon: '📝' },
        { path: '/openai', label: 'AI Chat Interface', icon: '💬' },
        { path: '/runner-settings', label: 'Runner Settings', icon: '⚙️' }
      ]
    }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs.current[openDropdown] && 
          !dropdownRefs.current[openDropdown].contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const handleDropdownToggle = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const handleItemClick = () => {
    setOpenDropdown(null);
  };

  const isItemActive = (items) => {
    return items.some(item => item.path === currentPath);
  };

  return (
    <nav className="modern-nav">
      <div className="nav-container">
        <Link to="/" className="nav-brand">
          <span className="brand-icon">🚀</span>
          <span className="brand-text">evoML & Artemis Platform</span>
        </Link>
        
        <div className="nav-menu">
          {menuItems.map((menu) => (
            <div 
              key={menu.label} 
              className="nav-dropdown"
              ref={el => dropdownRefs.current[menu.label] = el}
            >
              <button
                className={`nav-dropdown-toggle ${isItemActive(menu.items) ? 'active' : ''} ${openDropdown === menu.label ? 'open' : ''}`}
                onClick={() => handleDropdownToggle(menu.label)}
              >
                <span className="dropdown-icon">{menu.icon}</span>
                <span className="dropdown-label">{menu.label}</span>
                <span className="dropdown-arrow">▼</span>
              </button>
              
              {openDropdown === menu.label && (
                <div className="dropdown-menu">
                  {menu.items.map((item) => 
                    item.external ? (
                      <a
                        key={item.path}
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dropdown-item"
                        onClick={handleItemClick}
                      >
                        <span className="item-icon">{item.icon}</span>
                        <span className="item-label">{item.label}</span>
                      </a>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`dropdown-item ${currentPath === item.path ? 'active' : ''}`}
                        onClick={handleItemClick}
                      >
                        <span className="item-icon">{item.icon}</span>
                        <span className="item-label">{item.label}</span>
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navigation; 